import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  Briefcase,
  ChevronDown,
  User,
  Search,
  Code,
  Cpu,
  BarChart2,
  Palette,
} from "lucide-react";
import TeamHeader from "@/components/TeamHeader";
import { Reveal } from "@/components/Reveal";
import { TEAM } from "@/data/site";

export default function Team() {
  const [searchQuery, setSearchQuery] = useState("");

  // Dynamically segment and filter team members into precise org chart buckets
  const {
    ceo,
    digitalLead,
    developers,
    dataAnalyst,
    aiEngineer,
    multimedia,
    hasResults,
  } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = TEAM.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.role.toLowerCase().includes(query) ||
        (m.dept && m.dept.toLowerCase().includes(query))
    );

    const ceo = filtered.find(
      (m) =>
        m.role.toLowerCase().includes("ceo") ||
        m.role.toLowerCase().includes("chief executive")
    );

    const digitalLead = filtered.find(
      (m) => m.role.toLowerCase().includes("director")
    );

    const developers = filtered.filter((m) =>
      m.role.toLowerCase().includes("developer")
    );

    const dataAnalyst = filtered.filter((m) =>
      m.role.toLowerCase().includes("data analyst")
    );

    const aiEngineer = filtered.filter((m) =>
      m.role.toLowerCase().includes("ai engineer")
    );

    const multimedia = filtered.filter(
      (m) =>
        m.role.toLowerCase().includes("multimedia")
    );

    const hasResults =
      Boolean(ceo) ||
      Boolean(digitalLead) ||
      developers.length > 0 ||
      dataAnalyst.length > 0 ||
      aiEngineer.length > 0 ||
      multimedia.length > 0;

    return {
      ceo,
      digitalLead,
      developers,
      dataAnalyst,
      aiEngineer,
      multimedia,
      hasResults,
    };
  }, [searchQuery]);

  return (
    <div data-testid="team-page" className="bg-slate-50/50 min-h-screen">
      <TeamHeader />

      {/* Main Container */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-28 md:pb-36 border-b border-slate-200 overflow-hidden">
        {/* Section Header */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-michma-soft px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-michma font-extrabold mb-4 border border-michma/20">
              <Layers className="w-4 h-4" /> The Executive Team
            </span>
            <h2 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight text-michma-ink">
              Organizational Hierarchy
            </h2>
            <p className="mt-4 text-slate-700 text-xl leading-relaxed">
              An interconnected leadership framework structured for fiduciary compliance, strategic direction, and digital execution.
            </p>
          </div>
        </Reveal>

        {/* Search Control */}
        <div className="mb-16 max-w-md mx-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, role, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-slate-200/80 text-sm shadow-xs focus:outline-none focus:border-michma focus:ring-1 focus:ring-michma transition-all"
            />
          </div>
        </div>

        {/* Dynamic Org Chart */}
        {hasResults ? (
          <div className="relative max-w-5xl mx-auto">
            <div className="relative z-10 mb-12">
              <Reveal>
                <div className="flex items-center justify-center gap-2 mb-8">
                  <Briefcase className="w-6 h-6 text-michma-coral" />
                  <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma-coral font-extrabold">
                    Executive Operational Command
                  </span>
                </div>

                {/* 1. CEO Card */}
                {ceo && (
                  <div className="max-w-md mx-auto mb-6">
                    <Link
                      to={`/team/${ceo.slug}`}
                      data-testid={`team-member-${ceo.slug}`}
                      className="group block relative rounded-3xl border-2 border-michma-coral bg-orange-50/40 p-6 text-center hover:bg-white hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden border-2 border-michma-coral bg-slate-100 flex items-center justify-center">
                        {ceo.image ? (
                          <img
                            src={ceo.image}
                            alt={ceo.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <User className="w-10 h-10 text-slate-400" />
                        )}
                      </div>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma-coral font-bold block mb-1">
                        Executive Leadership
                      </span>
                      <h3 className="font-heading font-extrabold text-2xl text-michma-ink group-hover:text-michma-coral transition-colors">
                        {ceo.name}
                      </h3>
                      <div className="mt-3 inline-block rounded-full bg-michma-coral text-white px-4 py-1 font-mono text-xs font-extrabold uppercase tracking-wider">
                        {ceo.role}
                      </div>
                    </Link>
                  </div>
                )}

                {/* Connecting Flow Arrow */}
                {digitalLead && (
                  <div className="relative my-6 flex flex-col items-center justify-center">
                    <div className="w-1 h-8 bg-slate-300" />
                    <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-slate-300 flex items-center justify-center text-slate-600 shadow-md -mt-1">
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                )}

                {/* 2. Director Digital Card */}
                {digitalLead && (
                  <div className="max-w-md mx-auto mb-10">
                    <Link
                      to={`/team/${digitalLead.slug}`}
                      data-testid={`team-member-${digitalLead.slug}`}
                      className="group block relative rounded-3xl bg-white border-2 border-slate-200 p-6 text-center hover:border-michma-coral/60 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-2 border-michma-coral bg-slate-100 flex items-center justify-center">
                        {digitalLead.image ? (
                          <img
                            src={digitalLead.image}
                            alt={digitalLead.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <User className="w-8 h-8 text-slate-400" />
                        )}
                      </div>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 block mb-1 font-bold">
                        Digital Directorate
                      </span>
                      <h4 className="font-heading font-extrabold text-xl text-michma-ink group-hover:text-michma-coral transition-colors">
                        {digitalLead.name}
                      </h4>
                      <p className="text-xs text-slate-700 mt-2 font-bold uppercase tracking-wider">
                        {digitalLead.role}
                      </p>
                    </Link>
                  </div>
                )}

                {/* Connecting Flow Arrow to Sub-teams */}
                {(developers.length > 0 || dataAnalyst.length > 0 || aiEngineer.length > 0 || multimedia.length > 0) && (
                  <div className="relative my-6 flex flex-col items-center justify-center">
                    <div className="w-1 h-8 bg-slate-300" />
                    <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-slate-300 flex items-center justify-center text-slate-600 shadow-md -mt-1">
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                )}

                {/* 3. Technical & Operational Sub-teams Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 pt-4">
                  {[...developers, ...dataAnalyst, ...aiEngineer, ...multimedia].map((exec, i) => (
                    <Reveal key={exec.slug || i} delay={0.1 + i * 0.05}>
                      <Link
                        to={`/team/${exec.slug}`}
                        data-testid={`team-member-${exec.slug}`}
                        className="group relative rounded-2xl bg-white border border-slate-200/80 p-5 hover:border-michma-coral hover:shadow-lg transition-all duration-300 text-center flex flex-col items-center justify-between h-full"
                      >
                        <div className="w-full flex flex-col items-center">
                          <div className="relative w-20 h-20 mb-3 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                            {exec.image ? (
                              <img
                                src={exec.image}
                                alt={exec.name}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            ) : (
                              <User className="w-8 h-8 text-slate-400" />
                            )}
                          </div>
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 block mb-1 font-bold">
                            {exec.role.toLowerCase().includes("developer") && "Engineering"}
                            {exec.role.toLowerCase().includes("data") && "Analytics"}
                            {exec.role.toLowerCase().includes("ai") && "Artificial Intelligence"}
                            {exec.role.toLowerCase().includes("multimedia") && "Creative & IT"}
                          </span>
                          <h4 className="font-heading font-extrabold text-base text-michma-ink group-hover:text-michma-coral transition-colors">
                            {exec.name}
                          </h4>
                        </div>
                        <div className="mt-3 pt-2 border-t border-slate-100 w-full">
                          <span className="inline-block px-3 py-1 bg-slate-100 rounded-full font-mono text-[11px] font-semibold text-slate-700">
                            {exec.role}
                          </span>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        ) : (
          /* Empty Search Results State */
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 max-w-xl mx-auto shadow-xs">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-extrabold text-2xl text-michma-ink">
              No matching officers found
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              Try adjusting your search term to find active team members.
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-6 font-mono text-xs uppercase tracking-wider font-bold text-michma hover:underline cursor-pointer"
            >
              Reset Search
            </button>
          </div>
        )}
      </section>
    </div>
  );
}